import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";
import Icon from "@mdi/react";
import { mdiAccountPlus, mdiClose } from "@mdi/js";
import { toast } from "react-toastify";
import { classApi } from "../../api/classes";
import CustomFormInput from "../forms/CustomFormInput";
import "./AddStudentModal.css";

/**
 * RegisterStudentModal — register a brand-new student at the school (profile only, no class).
 * Used on the Students page "Add Student" entry point. After a successful register, the parent
 * chains into the existing ManualEnrolModal (passed the created student) to pick classes.
 *
 * Reuses AddStudentModal.css for identical look/feel. Creates a profile-only record via the
 * bulk-import endpoint with an empty enrolments[] array, then optionally sends an account invite.
 */
const RegisterStudentModal = ({ onClose, onCreated }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [sendInvite, setSendInvite] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    const handleSubmit = async () => {
        if (!firstName.trim()) {
            toast.error("First name is required");
            return;
        }
        if (!lastName.trim()) {
            toast.error("Last name is required");
            return;
        }
        if (!email.trim()) {
            toast.error("Email is required");
            return;
        }
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        try {
            const student = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
            };
            const response = await classApi.bulkImportStudents([student], []);
            if (!response.ok) {
                toast.error(response.data?.message || "Failed to register student");
                return;
            }
            const newId = response.data?.newProfileIds?.[0];
            if (!newId) {
                toast.error("Student could not be registered. They may already exist.");
                return;
            }

            if (sendInvite) {
                try {
                    await classApi.bulkSendInvites([newId]);
                } catch {
                    // Non-fatal — profile already created; surface a soft warning
                    toast.warn(`${student.firstName} registered, but the invite email could not be sent.`);
                }
            }

            toast.success(`${student.firstName} ${student.lastName} registered`);
            onCreated({ id: newId, firstName: student.firstName, lastName: student.lastName });
        } catch {
            toast.error("Failed to register student");
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="add-student-modal-overlay" onClick={onClose}>
            <div className="add-student-modal" onClick={e => e.stopPropagation()}>
                <div className="add-student-modal-header">
                    <h2>
                        <Icon path={mdiAccountPlus} size={1} color="var(--dl-color-purple-default)" style={{ marginRight: 8 }} />
                        Add Student
                    </h2>
                    <button className="add-student-modal-close" onClick={onClose}>
                        <Icon path={mdiClose} size={0.9} />
                    </button>
                </div>

                <div className="add-student-form">
                    <div className="add-student-walkin-hint">
                        This registers a new student at your school. Next you can enrol them in a class.
                    </div>
                    <label className="add-student-label">First Name *</label>
                    <input
                        type="text"
                        className="add-student-input"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="First name"
                        autoFocus
                    />

                    <label className="add-student-label">Last Name *</label>
                    <input
                        type="text"
                        className="add-student-input"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="Last name"
                    />

                    <label className="add-student-label">
                        Email <span className="add-student-required">*</span>
                    </label>
                    <input
                        type="email"
                        className="add-student-input"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="email@example.com"
                        required
                    />

                    <CustomFormInput type="checkbox" variant="turquoise"
                        label="Send Dance Localé account invite"
                        checked={sendInvite}
                        onChange={e => setSendInvite(e.target.checked)}
                        disabled={!email.trim()} />
                    <p className="add-student-hint">Emails them a link to set up their own Dance Localé account, where they can view their enrolments and bookings.</p>
                </div>

                <div className="add-student-modal-actions">
                    <button className="add-student-back-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="add-student-submit-btn"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !firstName.trim() || !lastName.trim()}>
                        {isSubmitting ? "Registering..." : "Register & Choose Classes"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RegisterStudentModal;
